import * as React from "react";
import copy from "copy-to-clipboard";
import { Check, ChevronRight, Copy, ExternalLink } from "lucide-react";
import {
  Alert,
  Badge,
  Button,
  Collapsible,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from "@/components/ui";
import { formatRelativeTime } from "@/components/reviews";
import { cn } from "@/lib/utils";
import { apiErrorMessage, isApiError } from "@/services/bedrock/apiError";
import { DesignPackageDetail } from "@/services/bedrock/dtos/DesignPackage";
import { DesignPackageActivityEvent } from "@/services/bedrock/dtos/DesignPackageActivity";
import {
  DesignPackageTrace,
  DesignPackageTraceEvent,
  TraceJobEvent,
  TraceRender,
  TraceRenderGroupEvent,
} from "@/services/bedrock/dtos/DesignPackageTrace";
import { useSocialCoachSession } from "@/utils/useSocialCoachSession";
import { useDesignPackageTrace } from "../hooks/useDesignPackageTrace";
import { ACTIVITY_LABELS } from "../thread";
import { activityDetails } from "./ActivityPanel";

interface TracePanelProps {
  pkg: DesignPackageDetail | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export interface TraceProblem {
  key: string;
  tone: "error" | "warning";
  title: string;
  detail?: string | null;
}

/** "Design::GeneratePackageJob" → "GeneratePackageJob". */
export function shortJobClass(jobClass: string): string {
  return jobClass.split("::").pop() ?? jobClass;
}

export function formatDuration(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds)) return null;
  if (seconds < 60) return `${seconds < 10 ? seconds.toFixed(1) : Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return rest === 0 ? `${minutes}m` : `${minutes}m ${rest}s`;
}

export function jobDurationSeconds(job: Pick<TraceJobEvent, "at" | "finishedAt">): number | null {
  if (!job.finishedAt) return null;
  const diff = new Date(job.finishedAt).getTime() - new Date(job.at).getTime();
  return Number.isFinite(diff) ? diff / 1000 : null;
}

const MINUTE_MS = 60_000;
/** Healthy generations take 60–150 s and refines 20–60 s, so younger jobs aren't flagged. */
const STUCK_AFTER_MS: Partial<Record<TraceJobEvent["status"], number>> = {
  running: 5 * MINUTE_MS,
  ready: 2 * MINUTE_MS,
  blocked: 2 * MINUTE_MS,
};
const IN_FLIGHT_STUCK_AFTER_MS = 5 * MINUTE_MS;

function minutesSince(iso: string, now: number): number {
  return Math.max(0, Math.floor((now - new Date(iso).getTime()) / MINUTE_MS));
}

export function inFlightLabel(inFlight: NonNullable<DesignPackageDetail["inFlight"]>): string {
  const kind = inFlight.kind === "refine" ? "Refine" : "Generate";
  const actor = inFlight.actor.onBehalfOf
    ? `${inFlight.actor.name} (as ${inFlight.actor.onBehalfOf.name})`
    : inFlight.actor.name;
  return `${kind} by ${actor}`;
}

/**
 * Everything that answers "where did it stop": failed jobs, jobs stuck past a threshold, flagged
 * renders, failed slides, an in-flight refine that's run too long, and a refine that never
 * reported its new version.
 */
export function traceProblems(
  trace: DesignPackageTrace,
  pkg?: Pick<DesignPackageDetail, "inFlight">,
  now: number = Date.now()
): TraceProblem[] {
  const problems: TraceProblem[] = [];
  const jobs = trace.events.filter((e): e is TraceJobEvent => e.type === "job");
  const retried = (job: TraceJobEvent) =>
    jobs.some(
      other =>
        other !== job &&
        other.jobClass === job.jobClass &&
        other.status === "finished" &&
        new Date(other.at).getTime() > new Date(job.at).getTime()
    );

  if (pkg?.inFlight && now - new Date(pkg.inFlight.startedAt).getTime() > IN_FLIGHT_STUCK_AFTER_MS) {
    problems.push({
      key: "in-flight",
      tone: "warning",
      title: `${inFlightLabel(pkg.inFlight)} running for ${minutesSince(pkg.inFlight.startedAt, now)} min`,
      detail: "The backend fails the slide after 30 min.",
    });
  }

  for (const event of trace.events) {
    if (event.type === "job") {
      const name = shortJobClass(event.jobClass);
      const stuckAfter = STUCK_AFTER_MS[event.status];
      if (event.status === "failed") {
        const recovered = retried(event);
        problems.push({
          key: `job-${event.activeJobId}`,
          tone: recovered ? "warning" : "error",
          title: recovered ? `${name} failed, then retried` : `${name} failed`,
          detail: event.error ? `${event.error.exceptionClass}: ${event.error.message}` : null,
        });
      } else if (stuckAfter != null && now - new Date(event.at).getTime() > stuckAfter) {
        problems.push({
          key: `job-${event.activeJobId}`,
          tone: "warning",
          title: `${name} ${event.status} for ${minutesSince(event.at, now)} min`,
          detail: `Since ${formatTime(event.at)} on ${event.queueName}`,
        });
      }
    } else if (event.type === "render_group") {
      for (const render of event.renders) {
        if (!render.stalled && !render.timedOut) continue;
        problems.push({
          key: `render-${render.designRenderId}`,
          tone: render.timedOut ? "error" : "warning",
          title: `Render ${render.designRenderId} (slide ${render.slideIndex}) ${
            render.timedOut ? "timed out" : "stalled"
          }`,
          detail: render.timedOut
            ? "The reaper failed it after the timeout."
            : "Still pending past the reaper's timeout; the next reaper run will fail it.",
        });
      }
    }
  }

  // A refine whose job ended without filling in the new template ids leaves the thread on "Rendering…".
  const workRunning = pkg?.inFlight != null || jobs.some(j => j.status === "running" || j.status === "ready");
  if (!workRunning) {
    const failedItemIds = new Set(trace.items.filter(i => i.status === "failed").map(i => i.id));
    trace.events.forEach((event, index) => {
      if (event.type !== "activity" || event.action !== "refined") return;
      const templateIds = event.payload.designTemplateIds;
      const itemIds = (event.payload.designPackageItemIds as number[] | undefined) ?? [];
      if (!Array.isArray(templateIds) || templateIds.length > 0) return;
      // A failed slide is already reported below.
      if (itemIds.some(id => failedItemIds.has(id))) return;
      problems.push({
        key: `refine-${index}`,
        tone: "error",
        title: "Refine never reported its new version",
        detail: typeof event.payload.instructions === "string" ? `“${event.payload.instructions}”` : null,
      });
    });
  }

  for (const item of [...trace.items].sort((a, b) => a.position - b.position)) {
    if (item.status !== "failed") continue;
    problems.push({
      key: `item-${item.id}`,
      tone: "error",
      title: `Slide ${item.position} failed`,
      detail: item.progressStatus,
    });
  }
  return problems;
}

function formatTime(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

const STATUS_TONES: Record<string, string> = {
  failed: "bg-red-100 text-red-700",
  finished: "bg-emerald-100 text-emerald-700",
  success: "bg-emerald-100 text-emerald-700",
  completed: "bg-emerald-100 text-emerald-700",
  running: "bg-sky-100 text-sky-700",
  processing: "bg-sky-100 text-sky-700",
  ready: "bg-amber-100 text-amber-800",
  pending: "bg-amber-100 text-amber-800",
  scheduled: "bg-amber-100 text-amber-800",
  blocked: "bg-amber-100 text-amber-800",
};

const TraceStatus: React.FC<{ status: string }> = ({ status }) => (
  <Badge variant="label" className={cn("whitespace-nowrap", STATUS_TONES[status])} data-testid="trace-status">
    {status}
  </Badge>
);

const CopyValue: React.FC<{ value: string; label: string }> = ({ value, label }) => {
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);
  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      <code className="truncate font-mono text-xs text-slate-600">{value}</code>
      <button
        type="button"
        className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        aria-label={`Copy ${label}`}
        onClick={() => {
          copy(value);
          setCopied(true);
        }}
      >
        {copied ? <Check className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
      </button>
    </span>
  );
};

const Section: React.FC<React.PropsWithChildren<{ title: string; testId: string }>> = ({ title, testId, children }) => (
  <section className="flex flex-col gap-2" data-testid={testId}>
    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
    {children}
  </section>
);

type Detail = DesignPackageDetail | undefined;

/** "Slide 2", "Slides 1, 3", or "all slides" when every slide is included. */
export function inFlightSlides(inFlight: NonNullable<DesignPackageDetail["inFlight"]>, detail: Detail): string {
  const items = detail?.items ?? [];
  if (inFlight.itemIds.length > 1 && items.length > 0 && items.every(i => inFlight.itemIds.includes(i.id)))
    return "all slides";
  const positions = inFlight.itemIds
    .map(id => items.find(i => i.id === id)?.position)
    .filter((p): p is number => p != null)
    .sort((a, b) => a - b);
  if (positions.length === 0) return inFlight.itemIds.length > 1 ? "all slides" : "the design";
  return positions.length === 1 ? `Slide ${positions[0]}` : `Slides ${positions.join(", ")}`;
}

const Summary: React.FC<{ trace: DesignPackageTrace; detail: Detail }> = ({ trace, detail }) => {
  const { package: pkg } = trace;
  const inFlight = detail?.inFlight;
  return (
    <Section title="Summary" testId="trace-summary">
      <div className="flex flex-wrap items-center gap-2">
        <TraceStatus status={pkg.status} />
        {pkg.progressStatus && <span className="text-sm text-slate-700">{pkg.progressStatus}</span>}
        {pkg.progressPercent != null && <span className="text-sm text-slate-500">{pkg.progressPercent}%</span>}
      </div>
      {inFlight && (
        <p className="text-sm text-slate-700" data-testid="trace-in-flight">
          {inFlightLabel(inFlight)} · {inFlightSlides(inFlight, detail)} · started{" "}
          {formatRelativeTime(inFlight.startedAt)}
        </p>
      )}
      {pkg.failureDetails && (
        <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded bg-red-50 p-2 text-xs text-red-800">
          {pkg.failureDetails}
        </pre>
      )}
      {trace.traceIds.length > 0 && (
        <ul className="flex flex-col gap-1" aria-label="Trace ids">
          {trace.traceIds.map(id => (
            <li key={id} className="flex min-w-0">
              <CopyValue value={id} label={`trace id ${id}`} />
            </li>
          ))}
        </ul>
      )}
      {trace.renderGroupCount > trace.renderGroupsLimit && (
        <p className="text-xs text-slate-500">
          Showing newest {trace.renderGroupsLimit} of {trace.renderGroupCount} render groups
        </p>
      )}
      {trace.logsUrl && (
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          nativeButton={false}
          render={<a href={trace.logsUrl} target="_blank" rel="noopener noreferrer" />}
        >
          Open in Logs Explorer
          <ExternalLink aria-hidden="true" />
        </Button>
      )}
    </Section>
  );
};

const Problems: React.FC<{ trace: DesignPackageTrace; detail: Detail }> = ({ trace, detail }) => {
  const problems = traceProblems(trace, detail);
  return (
    <Section title="Problems" testId="trace-problems">
      {problems.length === 0 ? (
        <p className="text-sm text-slate-500">No failed, stuck or stalled work.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {problems.map(problem => (
            <li
              key={problem.key}
              data-testid="trace-problem"
              className={cn(
                "rounded border px-3 py-2",
                problem.tone === "error" ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"
              )}
            >
              <p className={cn("text-sm font-medium", problem.tone === "error" ? "text-red-800" : "text-amber-900")}>
                {problem.title}
              </p>
              {problem.detail && <p className="break-words text-xs text-slate-600">{problem.detail}</p>}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
};

const RenderRow: React.FC<{ render: TraceRender }> = ({ render }) => (
  <li className="flex flex-col gap-0.5 border-l border-slate-200 pl-3" data-testid="trace-render">
    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-700">
      <span>Slide {render.slideIndex}</span>
      <TraceStatus status={render.status} />
      {render.stalled && (
        <Badge variant="label" className="bg-amber-100 text-amber-800">
          stalled
        </Badge>
      )}
      {render.timedOut && (
        <Badge variant="label" className="bg-red-100 text-red-700">
          timed out
        </Badge>
      )}
      {formatDuration(render.durationSeconds) && (
        <span className="text-slate-500">{formatDuration(render.durationSeconds)}</span>
      )}
    </div>
    {render.requestId && <CopyValue value={render.requestId} label={`request id ${render.requestId}`} />}
  </li>
);

const RenderGroupRow: React.FC<{ event: TraceRenderGroupEvent }> = ({ event }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Collapsible.Trigger className="flex w-full flex-wrap items-center gap-2 text-left text-sm text-slate-700">
        <ChevronRight aria-hidden="true" className={cn("size-4 shrink-0 transition-transform", open && "rotate-90")} />
        <span className="font-medium">Render group {event.designRenderGroupId}</span>
        <span className="text-slate-500">{event.renderIntent}</span>
        <TraceStatus status={event.status} />
        {formatDuration(event.durationSeconds) && (
          <span className="text-xs text-slate-500">{formatDuration(event.durationSeconds)}</span>
        )}
        <span className="text-xs text-slate-500">
          {event.renders.length} {event.renders.length === 1 ? "render" : "renders"}
        </span>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <ul className="mt-2 ml-6 flex flex-col gap-2">
          {event.renders.map(render => (
            <RenderRow key={render.designRenderId} render={render} />
          ))}
        </ul>
      </Collapsible.Content>
    </Collapsible>
  );
};

const JobRow: React.FC<{ event: TraceJobEvent }> = ({ event }) => {
  const duration = formatDuration(jobDurationSeconds(event));
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
        <span className="font-medium" title={event.jobClass}>
          {shortJobClass(event.jobClass)}
        </span>
        <TraceStatus status={event.status} />
        {duration && <span className="text-xs text-slate-500">{duration}</span>}
        <span className="text-xs text-slate-400">{event.queueName}</span>
      </div>
      {event.error && <p className="break-words text-xs text-red-700">{event.error.message}</p>}
    </div>
  );
};

function TimelineEvent({ event, detail }: { event: DesignPackageTraceEvent; detail: Detail }) {
  switch (event.type) {
    case "package_created":
      return (
        <p className="text-sm text-slate-700">
          Package created{event.createdByUserId != null && ` by user ${event.createdByUserId}`}
        </p>
      );
    case "activity": {
      const details = activityDetails(
        {
          action: event.action,
          payload: event.payload as DesignPackageActivityEvent["payload"],
          designPackageItemId: event.designPackageItemId,
        },
        detail
      );
      return (
        <div className="flex flex-col gap-0.5">
          <p className="text-sm text-slate-700">
            {ACTIVITY_LABELS[event.action] ?? event.action}
            {event.actorUserId != null && (
              <span className="text-slate-500">
                {" "}
                · user {event.actorUserId}
                {event.onBehalfOfUserId != null && ` (as user ${event.onBehalfOfUserId})`}
              </span>
            )}
          </p>
          {details && <p className="truncate text-xs text-slate-500">{details}</p>}
        </div>
      );
    }
    case "job":
      return <JobRow event={event} />;
    case "render_group":
      return <RenderGroupRow event={event} />;
    default:
      return null;
  }
}

function eventKey(event: DesignPackageTraceEvent, index: number): string {
  switch (event.type) {
    case "job":
      return `job-${event.activeJobId}`;
    case "render_group":
      return `group-${event.designRenderGroupId}`;
    default:
      return `${event.type}-${index}`;
  }
}

const Timeline: React.FC<{ trace: DesignPackageTrace; detail: Detail }> = ({ trace, detail }) => (
  <Section title="Timeline" testId="trace-timeline">
    <ol className="flex flex-col gap-3">
      {trace.events.map((event, index) => (
        <li key={eventKey(event, index)} className="flex flex-col gap-0.5" data-testid="trace-event">
          <span className="text-xs text-slate-400">{formatTime(event.at)}</span>
          <TimelineEvent event={event} detail={detail} />
        </li>
      ))}
    </ol>
  </Section>
);

const ADMINS_ONLY = <p className="text-sm text-slate-600">Trace is available to admins only.</p>;

const TraceBody: React.FC<{ pkg: DesignPackageDetail | undefined; open: boolean; isAdmin: boolean }> = ({
  pkg,
  open,
  isAdmin,
}) => {
  // Fetches and polls only while the sheet is open.
  const { data, error, isLoading, refetch, isFetching } = useDesignPackageTrace(pkg, { enabled: open && isAdmin });

  if (!isAdmin) return ADMINS_ONLY;

  // A background refetch that fails keeps showing the last trace.
  if (!data && error) {
    if (isApiError(error, "forbidden")) return ADMINS_ONLY;
    return (
      <Alert variant="destructive">
        <Alert.Description className="flex flex-col items-start gap-2">
          <span>{apiErrorMessage(error, "Couldn't load the trace.")}</span>
          <Button variant="outline" size="sm" loading={isFetching} onClick={() => void refetch()}>
            Retry
          </Button>
        </Alert.Description>
      </Alert>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex flex-col gap-3" data-testid="trace-loading">
        {[0, 1, 2].map(i => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Summary trace={data} detail={pkg} />
      <Problems trace={data} detail={pkg} />
      <Timeline trace={data} detail={pkg} />
    </div>
  );
};

/** Admin-only view of a package's jobs, renders and activity, for finding where a create or refine stopped. */
export const TracePanel: React.FC<TracePanelProps> = ({ pkg, open, onOpenChange }) => {
  const { isAdmin } = useSocialCoachSession();
  return (
    <Sheet open={open} onOpenChange={next => onOpenChange(next)}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Trace</SheetTitle>
        </SheetHeader>
        <div className="px-4 pb-4">
          <TraceBody pkg={pkg} open={open} isAdmin={isAdmin} />
        </div>
      </SheetContent>
    </Sheet>
  );
};
